import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Tooltip,
  IconButton,
  TableRow,
  Paper,
  TablePagination,
  ListItem,
  Select,
  MenuItem
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import UpgradePlan from '../../../upgradePlan/UpgradePlan';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DetailsModal from './DetailsModal';
import TakeItOutModal from './TakeItOutModal';
import TakeItOutUpdateModal from './TakeItOutUpdateModal';
import StatCard from './StatCard';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProperties,
  selectProperties,
  getPropertyDashboard,
  getProperty
} from '../../../../store/slices/propertiesSlice';
import { selectViewing, getViewings, getTakeItOut } from '../../../../store/slices/viewingSlice';
import { useTranslation } from 'react-i18next';

const RequestedViewingsTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const { viewings } = useSelector(selectViewing);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rows = viewings.data.filter((item) => item.status !== 'canceled');

  const handleOpenModal = (row) => {
    dispatch(getProperty(row.property_id));
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 4 }}>
        {t('Requested viewings')}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Full Name')}</TableCell>
              <TableCell>
                {t('Score')}
                <Tooltip title="This is the score information" arrow>
                  <IconButton size="small" sx={{ padding: 0, ml: 1 }}>
                    <HelpIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>{t('Date')}</TableCell>
              <TableCell>{t('Time')}</TableCell>
              <TableCell>{t('Status')}</TableCell>
              <TableCell>{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.user_name}</TableCell>
                <TableCell>{row.score}</TableCell>
                <TableCell>{row.scheduled_date}</TableCell>
                <TableCell>{row.scheduled_time}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() => handleOpenModal(row)}
                    sx={{
                      height: '40px',
                      background: '#fff',
                      color: '#1C2024',
                      fontSize: '12px',
                      lineHeight: '16px',
                      border: '1px solid #00062E32',
                      boxShadow: 'none',
                      '&:hover': {
                        background: '#fff',
                        boxShadow: 'none',
                        cursor: 'point'
                      }
                    }}
                  >
                    {t('Details')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <DetailsModal open={modalOpen} handleClose={handleCloseModal} rowData={selectedRow} />
    </Box>
  );
};

const Dashboard = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isAfterViewings, setIsAfterViewings] = React.useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = React.useState('');
  const { viewings, getTakeItOut: takeItOutState } = useSelector(selectViewing);
  // console.log("🚀 ~ Dashboard ~ takeItOutState:", takeItOutState);
  // console.log("🚀 ~ Dashboard ~ viewings:", viewings);
  const { properties, propertyDashboard } = useSelector(selectProperties);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getViewings({}));
    dispatch(fetchProperties());
    dispatch(getTakeItOut());
  }, [dispatch]);

  useEffect(() => {
    if (takeItOutState?.data) {
      setIsAfterViewings(true);
    } else {
      setIsAfterViewings(null);
    }
  }, [takeItOutState]);

  useEffect(() => {
    if (properties?.data && properties.data.length > 0 && !selectedPropertyId) {
      setSelectedPropertyId(properties.data[0].id);
    }
  }, [properties, selectedPropertyId]);

  useEffect(() => {
    if (properties?.data && properties.data.length > 0 && !selectedPropertyId) {
      setSelectedPropertyId(properties.data[0].id);
    }
  }, [properties, selectedPropertyId]);

  useEffect(() => {
    if (selectedPropertyId) {
      dispatch(getPropertyDashboard(selectedPropertyId));
    }
  }, [dispatch, selectedPropertyId]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handlePropertyChange = (event) => {
    setSelectedPropertyId(event.target.value);
  };

  return (
    <Box sx={{ display: 'grid', gap: '32px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: '24px', fontWeight: '500' }}>
          {t('Dashboard')}
        </Typography>
        <UpgradePlan
          color="#3E63DD"
          dark
          title={t('Upgrade Plan')}
          text={t('Get access to AI application submission')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <ListItem sx={{ width: '400px', p: 0 }}>
          <Select
            fullWidth
            value={selectedPropertyId}
            defaultChecked={selectedPropertyId}
            displayEmpty
            variant="outlined"
            size="small"
            sx={{ flex: 1, height: 40 }}
            onChange={handlePropertyChange}
          >
            {properties?.data.map((property) => (
              <MenuItem key={property.id} value={property.id}>
                {property.address}
              </MenuItem>
            ))}
          </Select>
        </ListItem>

        {isAfterViewings ? (
          <Button
            variant="contained"
            onClick={() => handleOpenModal()}
            endIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M7.71143 13.5834L4.4166 10.2886L11.3058 3.39937C11.572 3.13312 11.8882 3 12.2543 3C12.6204 3 12.9366 3.13312 13.2028 3.39937L14.6006 4.79718C14.8669 5.06343 15 5.3796 15 5.74569C15 6.11179 14.8669 6.42796 14.6006 6.69421L7.71143 13.5834ZM3.79757 14.9812C3.57126 15.0345 3.37157 14.9746 3.19851 14.8015C3.02545 14.6284 2.96554 14.4287 3.01879 14.2024L3.63782 11.1872L6.79287 14.3422L3.79757 14.9812Z"
                  fill="#3E63DD"
                />
              </svg>
            }
            sx={{
              background: 'transparent',
              color: '#1C2024',
              boxShadow: 'none',
              '&:hover': {
                background: 'transparent',
                boxShadow: 'none'
              }
            }}
          >
            {`${t('Your ad will take it out after')} ${
              takeItOutState?.data[0]?.number || 0
            } ${t('viewings')}`}
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            startIcon={<DeleteOutlineIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              background: '#FCFCFD !important',
              color: '#1C2024',
              height: '40px',
              width: '135px',
              fontSize: '16px',
              lineHeight: '24px',
              border: '1px solid #646464',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                background: '#FCFCFD !important',
                boxShadow: 'none'
              }
            }}
          >
            {t('Take it out')}
          </Button>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        mb={2}
        sx={{ gap: '12px', flexWrap: 'wrap' }}
      >
        <StatCard
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M20.0306 6.21938L16.2806 2.46938C16.2109 2.39975 16.1282 2.34454 16.0371 2.3069C15.9461 2.26926 15.8485 2.24992 15.75 2.25H8.25C7.85218 2.25 7.47064 2.40804 7.18934 2.68934C6.90804 2.97064 6.75 3.35218 6.75 3.75V5.25H5.25C4.85218 5.25 4.47064 5.40804 4.18934 5.68934C3.90804 5.97064 3.75 6.35218 3.75 6.75V20.25C3.75 20.6478 3.90804 21.0294 4.18934 21.3107C4.47064 21.592 4.85218 21.75 5.25 21.75H15.75C16.1478 21.75 16.5294 21.592 16.8107 21.3107C17.092 21.0294 17.25 20.6478 17.25 20.25V18.75H18.75C19.1478 18.75 19.5294 18.592 19.8107 18.3107C20.092 18.0294 20.25 17.6478 20.25 17.25V6.75C20.2501 6.65148 20.2307 6.55391 20.1931 6.46286C20.1555 6.37182 20.1003 6.28908 20.0306 6.21938ZM15.75 20.25H5.25V6.75H12.4397L15.75 10.0603V17.985C15.75 17.9906 15.75 17.9953 15.75 18C15.75 18.0047 15.75 18.0094 15.75 18.015V20.25ZM18.75 17.25H17.25V9.75C17.2501 9.65148 17.2307 9.55391 17.1931 9.46286C17.1555 9.37182 17.1003 9.28908 17.0306 9.21937L13.2806 5.46938C13.2109 5.39975 13.1282 5.34454 13.0371 5.3069C12.9461 5.26926 12.8485 5.24992 12.75 5.25H8.25V3.75H15.4397L18.75 7.06031V17.25ZM13.5 14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H8.25C8.05109 15 7.86032 14.921 7.71967 14.7803C7.57902 14.6397 7.5 14.4489 7.5 14.25C7.5 14.0511 7.57902 13.8603 7.71967 13.7197C7.86032 13.579 8.05109 13.5 8.25 13.5H12.75C12.9489 13.5 13.1397 13.579 13.2803 13.7197C13.421 13.8603 13.5 14.0511 13.5 14.25ZM13.5 17.25C13.5 17.4489 13.421 17.6397 13.2803 17.7803C13.1397 17.921 12.9489 18 12.75 18H8.25C8.05109 18 7.86032 17.921 7.71967 17.7803C7.57902 17.6397 7.5 17.4489 7.5 17.25C7.5 17.0511 7.57902 16.8603 7.71967 16.7197C7.86032 16.579 8.05109 16.5 8.25 16.5H12.75C12.9489 16.5 13.1397 16.579 13.2803 16.7197C13.421 16.8603 13.5 17.0511 13.5 17.25Z"
                fill="#3358D4"
              />
            </svg>
          }
          title={propertyDashboard?.data?.requested_viewings || '0'}
          subtitle={t('Requested viewings')}
          borderColor="#3F8CFF"
          color="#3358D4"
          bgColor="#E1E9FF"
        />
        <StatCard
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
            >
              <path
                d="M16.9167 2.5H15.0417V1.875C15.0417 1.70924 14.9759 1.55027 14.8587 1.43306C14.7415 1.31585 14.5825 1.25 14.4167 1.25C14.251 1.25 14.092 1.31585 13.9748 1.43306C13.8576 1.55027 13.7917 1.70924 13.7917 1.875V2.5H7.54175V1.875C7.54175 1.70924 7.4759 1.55027 7.35869 1.43306C7.24148 1.31585 7.08251 1.25 6.91675 1.25C6.75099 1.25 6.59202 1.31585 6.47481 1.43306C6.3576 1.55027 6.29175 1.70924 6.29175 1.875V2.5H4.41675C4.08523 2.5 3.76728 2.6317 3.53286 2.86612C3.29844 3.10054 3.16675 3.41848 3.16675 3.75V16.25C3.16675 16.5815 3.29844 16.8995 3.53286 17.1339C3.76728 17.3683 4.08523 17.5 4.41675 17.5H16.9167C17.2483 17.5 17.5662 17.3683 17.8006 17.1339C18.0351 16.8995 18.1667 16.5815 18.1667 16.25V3.75C18.1667 3.41848 18.0351 3.10054 17.8006 2.86612C17.5662 2.6317 17.2483 2.5 16.9167 2.5ZM6.29175 3.75V4.375C6.29175 4.54076 6.3576 4.69973 6.47481 4.81694C6.59202 4.93415 6.75099 5 6.91675 5C7.08251 5 7.24148 4.93415 7.35869 4.81694C7.4759 4.69973 7.54175 4.54076 7.54175 4.375V3.75H13.7917V4.375C13.7917 4.54076 13.8576 4.69973 13.9748 4.81694C14.092 4.93415 14.251 5 14.4167 5C14.5825 5 14.7415 4.93415 14.8587 4.81694C14.9759 4.69973 15.0417 4.54076 15.0417 4.375V3.75H16.9167V6.25H4.41675V3.75H6.29175ZM16.9167 16.25H4.41675V7.5H16.9167V16.25ZM13.9214 9.55781C13.9795 9.61586 14.0256 9.68479 14.0571 9.76066C14.0886 9.83654 14.1047 9.91787 14.1047 10C14.1047 10.0821 14.0886 10.1635 14.0571 10.2393C14.0256 10.3152 13.9795 10.3841 13.9214 10.4422L10.1714 14.1922C10.1134 14.2503 10.0445 14.2964 9.96859 14.3279C9.89271 14.3593 9.81138 14.3755 9.72925 14.3755C9.64711 14.3755 9.56578 14.3593 9.48991 14.3279C9.41404 14.2964 9.34511 14.2503 9.28706 14.1922L7.41206 12.3172C7.29479 12.1999 7.2289 12.0409 7.2289 11.875C7.2289 11.7091 7.29479 11.5501 7.41206 11.4328C7.52934 11.3155 7.6884 11.2497 7.85425 11.2497C8.0201 11.2497 8.17916 11.3155 8.29644 11.4328L9.72925 12.8664L13.0371 9.55781C13.0951 9.4997 13.164 9.4536 13.2399 9.42215C13.3158 9.3907 13.3971 9.37451 13.4792 9.37451C13.5614 9.37451 13.6427 9.3907 13.7186 9.42215C13.7945 9.4536 13.8634 9.4997 13.9214 9.55781Z"
                fill="#3E9B4F"
              />
            </svg>
          }
          title={propertyDashboard?.data?.scheduled_viewings || '0'}
          subtitle={t('Scheduled viewings')}
          borderColor="#3F8CFF"
          color="#3E9B4F"
          bgColor="#DAF1DB"
        />
        <StatCard
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                d="M11.3256 14.8053C12.3308 14.1361 13.0939 13.1612 13.5022 12.0248C13.9105 10.8883 13.9422 9.65061 13.5926 8.49474C13.243 7.33887 12.5308 6.32618 11.5611 5.60642C10.5915 4.88666 9.41599 4.49805 8.20842 4.49805C7.00085 4.49805 5.82533 4.88666 4.85571 5.60642C3.88608 6.32618 3.17382 7.33887 2.82426 8.49474C2.47469 9.65061 2.50638 10.8883 2.91464 12.0248C3.32289 13.1612 4.08604 14.1361 5.09123 14.8053C3.27294 15.4755 1.72008 16.7156 0.664358 18.3406C0.608873 18.4231 0.570333 18.5158 0.550978 18.6133C0.531623 18.7108 0.53184 18.8111 0.551615 18.9085C0.571391 19.0059 0.610331 19.0984 0.666172 19.1807C0.722013 19.2629 0.79364 19.3332 0.876892 19.3875C0.960143 19.4418 1.05336 19.479 1.15112 19.497C1.24887 19.5149 1.34923 19.5133 1.44634 19.4921C1.54346 19.4709 1.63539 19.4307 1.71681 19.3737C1.79823 19.3167 1.86751 19.244 1.92061 19.16C2.6016 18.1126 3.53344 17.2519 4.63152 16.6561C5.72959 16.0603 6.95911 15.7482 8.20842 15.7482C9.45773 15.7482 10.6872 16.0603 11.7853 16.6561C12.8834 17.2519 13.8152 18.1126 14.4962 19.16C14.6062 19.3235 14.7762 19.4371 14.9693 19.4763C15.1624 19.5156 15.3632 19.4772 15.5282 19.3695C15.6933 19.2619 15.8093 19.0936 15.8513 18.9011C15.8933 18.7085 15.8578 18.5072 15.7525 18.3406C14.6968 16.7156 13.1439 15.4755 11.3256 14.8053ZM4.08342 10.1253C4.08342 9.30948 4.32535 8.51195 4.77861 7.8336C5.23187 7.15525 5.87611 6.62654 6.62985 6.31433C7.3836 6.00212 8.213 5.92043 9.01317 6.07959C9.81334 6.23875 10.5483 6.63162 11.1252 7.20851C11.7021 7.78541 12.095 8.52041 12.2542 9.32058C12.4133 10.1208 12.3316 10.9502 12.0194 11.7039C11.7072 12.4576 11.1785 13.1019 10.5001 13.5551C9.8218 14.0084 9.02427 14.2503 8.20842 14.2503C7.11478 14.2491 6.06629 13.8141 5.29298 13.0408C4.51966 12.2675 4.08466 11.219 4.08342 10.1253ZM23.784 19.3785C23.6175 19.4871 23.4145 19.5251 23.2199 19.4841C23.0253 19.4432 22.8549 19.3266 22.7462 19.16C22.0661 18.112 21.1343 17.2509 20.036 16.6554C18.9377 16.0598 17.7078 15.7487 16.4584 15.7503C16.2595 15.7503 16.0687 15.6713 15.9281 15.5307C15.7874 15.39 15.7084 15.1992 15.7084 15.0003C15.7084 14.8014 15.7874 14.6107 15.9281 14.47C16.0687 14.3293 16.2595 14.2503 16.4584 14.2503C17.0659 14.2498 17.6657 14.115 18.2151 13.8557C18.7645 13.5965 19.2498 13.2191 19.6364 12.7505C20.023 12.2819 20.3013 11.7337 20.4515 11.1451C20.6017 10.5565 20.62 9.94198 20.5051 9.34547C20.3903 8.74895 20.1451 8.18516 19.7871 7.69439C19.4291 7.20361 18.9672 6.79796 18.4342 6.50642C17.9013 6.21488 17.3105 6.04465 16.7042 6.00789C16.0978 5.97113 15.4908 6.06875 14.9265 6.29377C14.8345 6.33354 14.7355 6.35447 14.6353 6.35531C14.5351 6.35616 14.4357 6.33691 14.343 6.2987C14.2504 6.26049 14.1663 6.20409 14.0958 6.13284C14.0253 6.0616 13.9698 5.97694 13.9326 5.88388C13.8954 5.79082 13.8772 5.69124 13.8791 5.59103C13.881 5.49082 13.903 5.39201 13.9437 5.30044C13.9845 5.20887 14.0431 5.12639 14.1163 5.05788C14.1895 4.98938 14.2756 4.93623 14.3697 4.90158C15.661 4.38656 17.0974 4.36803 18.4016 4.84955C19.7058 5.33108 20.7855 6.27854 21.4324 7.50916C22.0793 8.73978 22.2475 10.1664 21.9046 11.5137C21.5618 12.861 20.7321 14.0336 19.5756 14.8053C21.3939 15.4755 22.9468 16.7156 24.0025 18.3406C24.1111 18.5072 24.1491 18.7102 24.1082 18.9048C24.0672 19.0994 23.9506 19.2698 23.784 19.3785Z"
                fill="#9E6C00"
              />
            </svg>
          }
          title={propertyDashboard?.data?.applicants || '0'}
          subtitle={t('Applicants')}
          borderColor="#3F8CFF"
          color="#9E6C00"
          bgColor="#FFFAB8"
        />
        <StatCard title="Test" borderColor="#3F8CFF" button />
      </Box>
      <Box mb={2}>
        <RequestedViewingsTable />
      </Box>
      {isAfterViewings ? (
        <TakeItOutUpdateModal
          defaultValue={takeItOutState?.data[0]?.number}
          open={modalOpen}
          handleClose={handleCloseModal}
        />
      ) : (
        <TakeItOutModal
          open={modalOpen}
          handleClose={handleCloseModal}
          // setIsAfterViewings={setIsAfterViewings}
        />
      )}
    </Box>
  );
};

export default Dashboard;
